import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import type { DataTableFilterOption } from '@r/types/index.ts';
import type { Table } from '@tanstack/react-table';

import { cn } from '@r/utils/utils.ts';
import { Button } from '@r/components/ui/button.tsx';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@r/components/ui/dialog.tsx';
import { Input } from '@r/components/ui/input.tsx';
import { RangeInput } from '@r/components/ui/range-input.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@r/components/ui/popover.tsx';

import { DataTableAdvancedFacetedFilter } from './data-table-advanced-faceted-filter.tsx';
import { useDebounce } from '@r/hooks/use-debounce.ts';
import { PlusCircleIcon } from 'lucide-react';
import { DateRangePicker } from '@r/components/ui/date-range-picker.tsx';
import { format as formatDate } from 'date-fns';

interface DataTableFilterItemProps<TData> {
  table: Table<TData>;
  selectedOption: DataTableFilterOption<TData>;
  setSelectedOptions: Dispatch<SetStateAction<DataTableFilterOption<TData>[]>>;
  defaultOpen: boolean;
}

export function DataTableFilterItem<TData>({
  table,
  selectedOption,
  setSelectedOptions,
  defaultOpen
}: DataTableFilterItemProps<TData>) {
  const column = table.getColumn(selectedOption.value ? String(selectedOption.value) : '');
  const columnFilters = table.getState().columnFilters;

  // Source of truth visuelle pour les facettes:
  // on lit directement le state global des filtres pour rester synchrone
  // apres reset/rehydratation, meme si la colonne garde un etat transitoire.
  const selectedValues = new Set(
    Array.isArray(columnFilters.find((item) => item.id === selectedOption.value)?.value)
      ? (columnFilters.find((item) => item.id === selectedOption.value)?.value as string[])
      : []
  );

  const isRangeFilter =
    selectedOption.value === 'acquiredPoints' ||
    selectedOption.value === 'balance' ||
    selectedOption.value === 'pointCoeff' ||
    selectedOption.value === 'amount' ||
    selectedOption.value === 'referenceQuantity' ||
    selectedOption.value === 'pointAmount' ||
    selectedOption.value === 'productsCount';
  const isDateRangeFilter = selectedOption.isDateRange;
  const rangeMinId = `${String(selectedOption.value)}Min`;
  const rangeMaxId = `${String(selectedOption.value)}Max`;
  const dateAfterId = `${String(selectedOption.value)}After`;
  const dateBeforeId = `${String(selectedOption.value)}Before`;
  // Etats locaux utilises pour piloter les inputs visuels.
  // Ils sont initialises depuis l'etat de table pour refleter les valeurs restaurees.
  const [value, setValue] = useState(
    String(columnFilters.find((item) => item.id === selectedOption.value)?.value ?? '')
  );
  const [rangeMin, setRangeMin] = useState(
    String(columnFilters.find((item) => item.id === rangeMinId)?.value ?? '')
  );
  const [rangeMax, setRangeMax] = useState(
    String(columnFilters.find((item) => item.id === rangeMaxId)?.value ?? '')
  );
  const [dateRange, setDateRange] = useState<{ from?: string; to?: string }>({
    from: String(columnFilters.find((item) => item.id === dateAfterId)?.value ?? '') || undefined,
    to: String(columnFilters.find((item) => item.id === dateBeforeId)?.value ?? '') || undefined
  });
  const debounceValue = useDebounce(value, 500);
  const debouncedRangeMin = useDebounce(rangeMin, 500);
  const debouncedRangeMax = useDebounce(rangeMax, 500);
  const [open, setOpen] = useState(defaultOpen);
  const isFacetedFilter = selectedOption.options.length > 0;
  const hasRangeValue = isRangeFilter && (rangeMin.length > 0 || rangeMax.length > 0);
  const hasDateRangeValue = isDateRangeFilter && (dateRange.from || dateRange.to);
  const hasActiveValue = isFacetedFilter
    ? selectedValues.size > 0
    : isRangeFilter
      ? hasRangeValue
      : isDateRangeFilter
        ? Boolean(hasDateRangeValue)
        : value.length > 0;

  useEffect(() => {
    // Synchronise l'UI lorsque les filtres de table changent
    // (retour de navigation, restauration localStorage, reset global).
    // Important: on n'ecrase pas la saisie en cours si la valeur est deja identique.
    if (isFacetedFilter) {
      // Evite les faux etats "actifs" apres reset global sur les filtres facettes.
      setValue((prev) => (prev.length === 0 ? prev : ''));
      return;
    }

    if (isRangeFilter) {
      const restoredMin = String(columnFilters.find((item) => item.id === rangeMinId)?.value ?? '');
      const restoredMax = String(columnFilters.find((item) => item.id === rangeMaxId)?.value ?? '');
      setRangeMin((prev) => (prev === restoredMin ? prev : restoredMin));
      setRangeMax((prev) => (prev === restoredMax ? prev : restoredMax));
      return;
    }

    if (isDateRangeFilter) {
      const restoredFrom = String(
        columnFilters.find((item) => item.id === dateAfterId)?.value ?? ''
      );
      const restoredTo = String(
        columnFilters.find((item) => item.id === dateBeforeId)?.value ?? ''
      );

      const nextDateRange = {
        from: restoredFrom || undefined,
        to: restoredTo || undefined
      };

      setDateRange((prev) =>
        prev.from === nextDateRange.from && prev.to === nextDateRange.to ? prev : nextDateRange
      );
      return;
    }

    const restoredValue = String(
      columnFilters.find((item) => item.id === selectedOption.value)?.value ?? ''
    );
    setValue((prev) => (prev === restoredValue ? prev : restoredValue));
  }, [
    columnFilters,
    selectedOption.value,
    isFacetedFilter,
    isRangeFilter,
    isDateRangeFilter,
    rangeMinId,
    rangeMaxId,
    dateAfterId,
    dateBeforeId
  ]);

  useEffect(() => {
    // Propage les valeurs saisies vers les columnFilters (avec debounce sur les champs texte/range).
    if (isFacetedFilter) {
      // Les filtres facettes sont geres uniquement par DataTableAdvancedFacetedFilter.
      return;
    }

    if (isRangeFilter) {
      table.setColumnFilters((prev) => {
        const newColumnFilters = prev.filter(
          (item) => item.id !== rangeMinId && item.id !== rangeMaxId
        );
        if (debouncedRangeMin.length > 0) {
          newColumnFilters.push({
            id: rangeMinId,
            value: debouncedRangeMin
          });
        }
        if (debouncedRangeMax.length > 0) {
          newColumnFilters.push({
            id: rangeMaxId,
            value: debouncedRangeMax
          });
        }
        return newColumnFilters;
      });
      return;
    }

    if (isDateRangeFilter) {
      table.setColumnFilters((prev) => {
        const newColumnFilters = prev.filter(
          (item) => item.id !== dateAfterId && item.id !== dateBeforeId
        );
        if (dateRange.from) {
          newColumnFilters.push({
            id: dateAfterId,
            value: dateRange.from
          });
        }
        if (dateRange.to) {
          newColumnFilters.push({
            id: dateBeforeId,
            value: dateRange.to
          });
        }
        return newColumnFilters;
      });
      return;
    }

    table.setColumnFilters((prev) => {
      const newColumnFilters = prev.filter((item) => item.id !== selectedOption.value);
      if (debounceValue.trim().length > 0) {
        newColumnFilters.push({
          id: selectedOption.value as string,
          value: debounceValue
        });
      }
      return newColumnFilters;
    });
  }, [
    debounceValue,
    debouncedRangeMin,
    debouncedRangeMax,
    isRangeFilter,
    isDateRangeFilter,
    isFacetedFilter,
    dateAfterId,
    dateBeforeId,
    dateRange.from,
    dateRange.to,
    rangeMinId,
    rangeMaxId
  ]);

  const triggerButton = (
    <Button
      variant="outline"
      className={cn(
        'h-7 gap-0 truncate rounded-md border-dashed p-2.5',
        hasActiveValue && 'bg-muted/50'
      )}
    >
      <PlusCircleIcon className="mr-2.5 w-4" />
      <span className="font-medium capitalize">{selectedOption.label}</span>
      {isFacetedFilter
        ? selectedValues.size > 0 && (
            <span className="text-muted-foreground">
              <span className="text-foreground">: </span>
              {selectedValues.size > 2
                ? `${selectedValues.size} selected`
                : selectedOption.options
                    .filter((item) => selectedValues.has(item.value))
                    .map((item) => item.label)
                    .join(', ')}
            </span>
          )
        : isRangeFilter
          ? (rangeMin.length > 0 || rangeMax.length > 0) && (
              <span className="text-muted-foreground">
                <span className="text-foreground">: </span>
                {rangeMin.length > 0 ? rangeMin : '…'} - {rangeMax.length > 0 ? rangeMax : '…'}
              </span>
            )
          : isDateRangeFilter
            ? (dateRange.from || dateRange.to) && (
                <span className="text-muted-foreground">
                  <span className="text-foreground">: </span>
                  {dateRange.from ?? '…'} - {dateRange.to ?? '…'}
                </span>
              )
            : value.length > 0 && (
                <span className="text-muted-foreground">
                  <span className="text-foreground">: </span>
                  {value}
                </span>
              )}
    </Button>
  );

  // Le calendrier (2 mois + presets) est trop grand pour un popover ancré au bouton :
  // on l'ouvre dans une modale centrée, toujours entièrement visible.
  if (isDateRangeFilter) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>{triggerButton}</DialogTrigger>
        <DialogContent className="max-h-[90vh] w-auto max-w-[95vw] overflow-y-auto p-4">
          <DialogTitle className="sr-only">{selectedOption.label}</DialogTitle>
          <DateRangePicker
            inline
            onClose={() => setOpen(false)}
            isActive={Boolean(dateRange.from && dateRange.to)}
            placeholder={selectedOption.placeholder ?? selectedOption.label}
            onUpdate={({ range }) => {
              if (!range.from || !range.to) {
                setDateRange({});
                return;
              }
              setDateRange({
                from: formatDate(range.from, 'yyyy-MM-dd'),
                to: formatDate(range.to, 'yyyy-MM-dd')
              });
            }}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
      <PopoverContent className="w-60 space-y-1.5 p-2" align="start">
        {selectedOption.options.length > 0 ? (
          column && (
            <DataTableAdvancedFacetedFilter
              key={String(selectedOption.value)}
              column={column}
              title={selectedOption.label}
              options={selectedOption.options}
              selectedValues={selectedValues}
              setSelectedOptions={setSelectedOptions}
            />
          )
        ) : isRangeFilter ? (
          <RangeInput
            minValue={rangeMin}
            maxValue={rangeMax}
            onMinChange={setRangeMin}
            onMaxChange={setRangeMax}
            inputClassName="h-8"
            autoFocus
          />
        ) : (
          <Input
            placeholder={selectedOption.placeholder ?? selectedOption.label}
            className="h-8"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            autoFocus
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
